import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useValidationState } from '../hooks/useValidationState';
import { useValidationWorkspace } from '../hooks/useValidationWorkspace';
import { useValidationResumeSync } from '../hooks/useValidationResumeSync';
import { useValidationActiveFilters } from '../hooks/useValidationActiveFilters';
import { useValidationDetailEmptyState } from '../hooks/useValidationDetailEmptyState';
import {
  useValidationEmbeddedActions,
  type RegisterValidationEmbeddedActions,
} from '../hooks/useValidationEmbeddedActions';
import ValidationEmptyState from './ValidationEmptyState';
import ConfigurationDialog from './ConfigurationDialog';
import ReconnectionDialog from './ReconnectionDialog';
import ValidationHeader from './ValidationHeader';
import ValidationIssueList from './ValidationIssueList';
import ValidationIssueDetailPanel from './ValidationIssueDetailPanel';
import WorkspacePageShell from '../../../shared/ui/workflow/WorkspacePageShell';
import ValidationGuideDialog from './ValidationGuideDialog';
import ValidationRunProgressWindowRequest from './ValidationRunProgressWindowRequest';
import RepairWorkspaceLoadingFallback from './repair-detail/RepairWorkspaceLoadingFallback';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useNavigation } from '../../../shared/lib/hooks/useNavigation';
import type { ValidationIssueFamily } from '../lib/validationIssueFamilies';
import type { ValidationRepairPanelTab } from '../../../shared/stores/usePcfValidationStore';
import { useWorkflowResumeStore } from '../../../shared/stores/useWorkflowResumeStore';
import {
  buildValidationIssueNavigationContext,
  resolveValidationPrimaryActionNavigation,
} from '../lib/validationActionRouting';
import { buildValidationRepairDetailItems } from '../lib/validationRepairDetailModel';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import {
  mapValidationFamilyToConditionTargetKey,
  resolveConditionStudioEntryForFamily,
} from '../lib/validationConditionStudioModel';
import WorkspaceSpotlightTour, {
} from '../../../shared/ui/common/WorkspaceSpotlightTour';
import { useHostRibbonChromeTargets } from '../../../shared/ui/common/hostRibbonChromeTargets';
import {
  buildValidationGuideContent,
  buildValidationTourSteps,
} from './validationGuideContent';
import { validationChromeControllers } from './validationChromeControllers';
import { getE2eBootstrapConfig } from '../../../shared/config/e2eBootstrap';
import {
  resolveValidationDisplayNumber,
  resolveValidationIdCriticalityRank,
} from '../validationConstants';
import { perf } from '../../../shared/lib/perfLogger';
import { layoutGap } from '../../../theme/theme';
import { getWorkspaceDrawerPaperSx } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { buildIssueClipboardText } from '../lib/validationClipboard';
import {
  hasSeenValidationOnboarding,
  markValidationOnboardingSeen,
} from '../lib/validationOnboarding';
import { validationCopy } from '../copy/validationCopy';
import { CaptionText } from '../../../shared/ui/typography';
import ValidationTriageRail from './ValidationTriageRail';


interface PcfValidationContentProps {
  onGoBack?: () => void;
  embedded?: boolean;
  registerEmbeddedActions?: RegisterValidationEmbeddedActions;
  resumeScopeKey?: string | null;
}

const E2E_BOOTSTRAP =
  typeof window === 'undefined'
    ? null
    : getE2eBootstrapConfig(window.location.search, process.env.NODE_ENV);
const DEFAULT_RECONNECT_GAP_MARGIN_SETTINGS = {
  reconnectGapMarginMillimeters: 20,
  defaultReconnectGapMarginMillimeters: 20,
  minReconnectGapMarginMillimeters: 1,
  maxReconnectGapMarginMillimeters: 50,
};
const ValidationConditionStudioPage = lazy(() => import('./ValidationConditionStudioPage'));
const ValidationRepairDetailPage = lazy(() => import('./ValidationRepairDetailPage'));


export default function PcfValidationContent({
  onGoBack,
  embedded = false,
  registerEmbeddedActions,
  resumeScopeKey,
}: PcfValidationContentProps) {
  const state = useValidationState();
  const { report, handleRunValidation } = state;
  const validationSettings = state.validationSettings ?? DEFAULT_RECONNECT_GAP_MARGIN_SETTINGS;
  const theme = useTheme();
  const railBreakpoint = embedded ? 'lg' : 'md';
  const detailBreakpoint = 'xl';
  const showRailInlineByWidth = useMediaQuery(theme.breakpoints.up(railBreakpoint));
  const isShortValidationViewport = useMediaQuery('(max-height:700px)');
  const showRailInline = showRailInlineByWidth && !isShortValidationViewport;
  const showInlineDetail = useMediaQuery(theme.breakpoints.up(detailBreakpoint));
  const showDrawerDetail = useMediaQuery(theme.breakpoints.down(detailBreakpoint));
  const focusedSystem = usePcfValidationStore((store) => store.focusedSystem);
  const clearFocusedSystem = usePcfValidationStore((store) => store.clearFocusedSystem);
  const requestWorkspaceTab = usePcfValidationStore((store) => store.requestWorkspaceTab);
  const setIssueNavigationContext = usePcfValidationStore((store) => store.setIssueNavigationContext);
  const clearIssueNavigationContext = usePcfValidationStore((store) => store.clearIssueNavigationContext);
  const issueNavigationContext = usePcfValidationStore((store) => store.issueNavigationContext);
  const repairDetailContext = usePcfValidationStore((store) => store.repairDetailContext);
  const repairHistory = usePcfValidationStore((store) => store.repairHistory);
  const repairItemStates = usePcfValidationStore((store) => store.repairItemStates);
  const openRepairDetail = usePcfValidationStore((store) => store.openRepairDetail);
  const closeRepairDetail = usePcfValidationStore((store) => store.closeRepairDetail);
  const setRepairDetailSelectedItem = usePcfValidationStore((store) => store.setRepairDetailSelectedItem);
  const setRepairDetailActiveTab = usePcfValidationStore((store) => store.setRepairDetailActiveTab);
  const setRepairSurgeryMode = usePcfValidationStore((store) => store.setRepairSurgeryMode);
  const addRepairHistoryEntry = usePcfValidationStore((store) => store.addRepairHistoryEntry);
  const setRepairItemState = usePcfValidationStore((store) => store.setRepairItemState);
  const conditionStudioContext = usePcfValidationStore((store) => store.conditionStudioContext);
  const conditionSets = usePcfValidationStore((store) => store.conditionSets);
  const createConditionSet = usePcfValidationStore((store) => store.createConditionSet);
  const openConditionStudio = usePcfValidationStore((store) => store.openConditionStudio);
  const validationResume = useWorkflowResumeStore((store) =>
    resumeScopeKey ? store.validationByScope[resumeScopeKey] : undefined
  );
  const { navigateTo, navigateToMappingConfiguration, currentScreen } = useNavigation();
  const snackbar = useSnackbarStore();
  const validationWorkspaceInitialState = useMemo(
    () =>
      validationResume
        ? {
            selectedFamilyId: validationResume.selectedFamilyId,
            searchQuery: validationResume.searchQuery,
            severityFilter: validationResume.severityFilter,
            blockerFilter: validationResume.blockerFilter,
            requiredOnly: validationResume.requiredOnly,
            categoryFilter: validationResume.categoryFilter,
            showPassedOnly: validationResume.showPassedOnly,
            passedCollapsed: validationResume.passedCollapsed,
          }
        : undefined,
    [validationResume]
  );
  const workspace = useValidationWorkspace(
    state.report?.CheckResults,
    focusedSystem?.elementIds,
    state.report?.DocumentName,
    validationWorkspaceInitialState,
    focusedSystem?.systemNames
  );
  const {
    allFamilies,
    selectedFamily,
    selectedFamilyId,
    setSelectedFamilyId,
    showPassedOnly,
    setShowPassedOnly,
    setSearchQuery,
    setSeverityFilter,
    setBlockerFilter,
    setRequiredOnly,
    setCategoryFilter,
  } = workspace;
  useValidationResumeSync(resumeScopeKey, workspace);
  const displayNumbersByFamilyId = useMemo(
    () =>
      allFamilies.reduce((map, family) => {
        const displayNumber = resolveValidationDisplayNumber(family.validationIds);
        if (displayNumber != null) {
          map.set(family.id, displayNumber);
        }
        return map;
      }, new Map<string, number>()),
    [allFamilies]
  );
  const selectedFamilyDisplayNumber = useMemo(() => {
    if (!selectedFamily) {
      return null;
    }

    return displayNumbersByFamilyId.get(selectedFamily.id) ?? null;
  }, [displayNumbersByFamilyId, selectedFamily]);
  const lastValidationLabel =
    state.report?.LastValidationCapturedAtLocal ||
    state.report?.LastValidationDate ||
    state.report?.ValidationDate;
  const requestedValidationSurface = E2E_BOOTSTRAP?.validationSurface;
  const requestedValidationRepairPath = E2E_BOOTSTRAP?.validationRepairPath ?? 'direct';
  const canRenderValidationWorkspace =
    state.isReady && (!state.documentInfoChecked || Boolean(state.documentInfo?.DocumentName));

  const [railOpen, setRailOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(showInlineDetail);
  const [guideOpen, setGuideOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const useInlineTourDrawers = tourOpen;
  const headerRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const issueListRef = useRef<HTMLDivElement | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const headerRunContextRef = useRef<HTMLDivElement | null>(null);
  const headerActionsRef = useRef<HTMLDivElement | null>(null);
  const headerGuideRef = useRef<HTMLButtonElement | null>(null);
  const headerConditionStudioRef = useRef<HTMLButtonElement | null>(null);
  const headerConfigureRef = useRef<HTMLButtonElement | null>(null);
  const headerRunRef = useRef<HTMLButtonElement | null>(null);
  const railToggleRef = useRef<HTMLButtonElement | null>(null);
  const detailToggleRef = useRef<HTMLButtonElement | null>(null);
  const railSearchRef = useRef<HTMLDivElement | null>(null);
  const railFiltersRef = useRef<HTMLDivElement | null>(null);
  const railRepairAreaRef = useRef<HTMLDivElement | null>(null);
  const railPassedRef = useRef<HTMLDivElement | null>(null);
  const shellRailResizeRef = useRef<HTMLDivElement | null>(null);
  const shellDetailResizeRef = useRef<HTMLDivElement | null>(null);
  const queueKeyboardHelpRef = useRef<HTMLButtonElement | null>(null);
  const queueSelectedCardRef = useRef<HTMLDivElement | null>(null);
  const queueSelectedCardActionRef = useRef<HTMLDivElement | null>(null);
  const detailRecommendedRef = useRef<HTMLDivElement | null>(null);
  const detailQuickFixRef = useRef<HTMLDivElement | null>(null);
  const detailClassificationRef = useRef<HTMLDivElement | null>(null);
  const detailFallbackGuidanceRef = useRef<HTMLDivElement | null>(null);
  const detailAlternatePathsRef = useRef<HTMLDivElement | null>(null);
  const detailImpactSummaryRef = useRef<HTMLDivElement | null>(null);
  const detailMoreDetailsRef = useRef<HTMLDivElement | null>(null);
  const detailAffectedItemsRef = useRef<HTMLDivElement | null>(null);
  const detailTopAffectedTypesRef = useRef<HTMLDivElement | null>(null);
  const detailSampleInstancesRef = useRef<HTMLDivElement | null>(null);
  const detailRawIssueDetailsRef = useRef<HTMLDivElement | null>(null);
  const detailCopyActionRef = useRef<HTMLDivElement | null>(null);
  const e2eValidationSurfaceAppliedRef = useRef(false);

  const handleCloseTour = useCallback(() => {
    setTourOpen(false);
    setTourStep(0);
  }, []);

  const ensureValidationPrimed = useCallback(async () => {
    return;
  }, []);

  // Keep chrome controller rects in sync with the host window size.
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const validationFallbackChromeTargets = useMemo(
    () => (
      typeof window !== 'undefined' && window.chrome?.webview
        ? validationChromeControllers(viewportWidth)
        : undefined
    ),
    [viewportWidth]
  );
  const validationHostChromeTargets = useHostRibbonChromeTargets(validationFallbackChromeTargets);

  const handleOpenGuide = useCallback(async () => {
    await ensureValidationPrimed();
    handleCloseTour();
    setGuideOpen(true);
  }, [ensureValidationPrimed, handleCloseTour]);

  const canOpenValidationTour = canRenderValidationWorkspace && Boolean(report);

  const openValidationTour = useCallback((rememberOnboarding: boolean) => {
    if (!canOpenValidationTour) {
      return;
    }

    setGuideOpen(false);
    setTourStep(0);
    setTourOpen(true);
    if (rememberOnboarding && !hasSeenValidationOnboarding()) {
      markValidationOnboardingSeen();
    }
  }, [canOpenValidationTour]);

  const handleOpenTour = useCallback(async () => {
    await ensureValidationPrimed();
    openValidationTour(true);
  }, [ensureValidationPrimed, openValidationTour]);
  const registerActiveValidationActions = useValidationEmbeddedActions({
    embedded,
    registerEmbeddedActions,
    openGuide: handleOpenGuide,
    openTour: () => void handleOpenTour(),
  });

  useEffect(() => {
    if (
      hasSeenValidationOnboarding() ||
      !canOpenValidationTour ||
      (requestedValidationSurface && requestedValidationSurface !== 'console')
    ) {
      return;
    }

    openValidationTour(true);
  }, [
    canOpenValidationTour,
    openValidationTour,
    requestedValidationSurface,
  ]);

  useEffect(() => {
    if (showInlineDetail) {
      setDetailOpen(true);
      return;
    }

    if (showDrawerDetail) {
      setDetailOpen(false);
      return;
    }

    setDetailOpen(true);
  }, [showDrawerDetail, showInlineDetail]);

  useEffect(() => {
    if (!issueNavigationContext?.familyId || showPassedOnly) {
      return;
    }

    const familyExists = allFamilies.some(
      (family) => family.id === issueNavigationContext.familyId
    );

    if (!familyExists || selectedFamilyId === issueNavigationContext.familyId) {
      return;
    }

    setSelectedFamilyId(issueNavigationContext.familyId);
  }, [
    allFamilies,
    issueNavigationContext?.familyId,
    selectedFamilyId,
    setSelectedFamilyId,
    showPassedOnly,
  ]);

  useEffect(() => {
    if (!repairDetailContext?.familyId || showPassedOnly) {
      return;
    }

    const detailFamilyExists = allFamilies.some((family) => family.id === repairDetailContext.familyId);
    if (!detailFamilyExists) {
      closeRepairDetail();
    }
  }, [allFamilies, closeRepairDetail, repairDetailContext?.familyId, showPassedOnly]);

  const activeFilters = useValidationActiveFilters(workspace);
  const hasActiveFilters =
    workspace.searchQuery.trim().length > 0 ||
    activeFilters.length > 0 ||
    workspace.blockerFilter === 'blocking' ||
    workspace.requiredOnly;
  const defaultConditionStudioFamily =
    workspace.selectedFamily || workspace.filteredFamilies[0] || workspace.allFamilies[0] || null;
  const defaultConditionStudioTargetKey = defaultConditionStudioFamily
    ? mapValidationFamilyToConditionTargetKey(defaultConditionStudioFamily)
    : 'required-export';

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSeverityFilter('all');
    setBlockerFilter('all');
    setRequiredOnly(false);
    setCategoryFilter('all');
    setShowPassedOnly(false);
  }, [
    setBlockerFilter,
    setCategoryFilter,
    setRequiredOnly,
    setSearchQuery,
    setSeverityFilter,
    setShowPassedOnly,
  ]);
  const detailEmptyState = useValidationDetailEmptyState(workspace, hasActiveFilters, handleClearFilters);

  const handleSelectFamily = (family: ValidationIssueFamily) => {
    workspace.setShowPassedOnly(false);
    workspace.setSelectedFamilyId(family.id);

    if (showDrawerDetail || !showInlineDetail) {
      setDetailOpen(true);
    }
  };

  const handleRunValidationFresh = useCallback(() => {
    void handleRunValidation(undefined, { forceFresh: true });
  }, [handleRunValidation]);

  const handleOpenMappingIssue = useCallback(() => {
    requestWorkspaceTab('mapping');
    navigateToMappingConfiguration();
  }, [navigateToMappingConfiguration, requestWorkspaceTab]);

  const handleExecutePrimaryAction = (family: ValidationIssueFamily) => {
    if (family.primaryAction.type === 'highlight') {
      if (family.impactedElementIds.length > 0) {
        void state.handleHighlightElements(family.impactedElementIds);
      }
      return;
    }

    if (family.primaryAction.type === 'detail') {
      workspace.setShowPassedOnly(false);
      workspace.setSelectedFamilyId(family.id);
      openRepairDetail({
        familyId: family.id,
        selectedItemId:
          repairDetailContext?.familyId === family.id
            ? repairDetailContext.selectedItemId
            : null,
        activeTab: 'recommended',
        surgeryMode: false,
      });
      if (showDrawerDetail || !showInlineDetail) {
        setDetailOpen(true);
      }
      return;
    }

    if (family.primaryAction.type === 'rerun') {
      clearIssueNavigationContext();
      handleRunValidationFresh();
      return;
    }

    const navigationContext = buildValidationIssueNavigationContext(currentScreen, family);
    perf.log('VAL-Handoff', 'Executing primary action', {
      familyId: family.id,
      title: family.title,
      targetPage: family.targetPage,
      actionRoute: family.primaryAction.route || family.primaryAction.type || 'none',
      requestId: navigationContext.quickFillFocus?.requestId,
      labels: navigationContext.quickFillFocus?.labels,
      elementIds: navigationContext.quickFillFocus?.elementIds,
      familyTypeKeys: navigationContext.quickFillFocus?.familyTypeKeys,
    });
    setIssueNavigationContext(navigationContext);

    if (family.targetPage === 'mapping' && family.primaryAction.type === 'navigate') {
      handleOpenMappingIssue();
      return;
    }

    const navigation = resolveValidationPrimaryActionNavigation(
      currentScreen,
      family.primaryAction,
      family.targetPage
    );

    perf.log('VAL-Handoff', 'Resolved navigation target', {
      familyId: family.id,
      navigateTo: navigation.navigateTo || null,
      requestWorkspaceTab: navigation.requestWorkspaceTab || null,
    });

    if (navigation.requestWorkspaceTab) {
      requestWorkspaceTab(navigation.requestWorkspaceTab);
    }

    if (navigation.navigateTo) {
      navigateTo(navigation.navigateTo);
    }
  };

  const handleOpenRepairDetail = (
    family: ValidationIssueFamily,
    options?: {
      activeTab?: ValidationRepairPanelTab;
      surgeryMode?: boolean;
    }
  ) => {
    workspace.setShowPassedOnly(false);
    workspace.setSelectedFamilyId(family.id);
    openRepairDetail({
      familyId: family.id,
      selectedItemId:
        repairDetailContext?.familyId === family.id
          ? repairDetailContext.selectedItemId
          : null,
      activeTab: options?.activeTab ?? (options?.surgeryMode ? 'surgery' : 'recommended'),
      surgeryMode: options?.surgeryMode ?? false,
    });
  };

  const handleOpenConditionStudio = useCallback(
    (family: ValidationIssueFamily) => {
      workspace.setShowPassedOnly(false);
      workspace.setSelectedFamilyId(family.id);

      const primaryValidationId = [...family.validationIds].sort(
        (left, right) => resolveValidationIdCriticalityRank(left) - resolveValidationIdCriticalityRank(right)
      )[0] ?? null;
      const primaryValidation =
        primaryValidationId != null
          ? state.validations.find((validation) => validation.Id === primaryValidationId) ?? null
          : null;
      const resolution = resolveConditionStudioEntryForFamily({
        report,
        family,
        conditionSets,
      });
      const matchedConditionSet =
        resolution.conditionSetId != null
          ? conditionSets.find((conditionSet) => conditionSet.id === resolution.conditionSetId) ?? null
          : null;
      const selectedConditionSetId =
        resolution.action === 'open-existing' && resolution.conditionSetId
          ? resolution.conditionSetId
          : createConditionSet(
              resolution.targetKey,
              primaryValidation
                ? {
                    validationId: primaryValidation.Id,
                    validationTitle: primaryValidation.Title,
                  }
                : undefined
            );

      openConditionStudio({
        targetKey: resolution.targetKey,
        selectedConditionSetId,
        selectedValidationId:
          resolution.action === 'open-existing'
            ? matchedConditionSet?.draft.validationId ??
              matchedConditionSet?.published?.validationId ??
              null
            : primaryValidation?.Id ?? null,
        sourceFamilyId: family.id,
      });

      if (resolution.action === 'open-existing') {
        snackbar.showInfo(validationCopy.snackbars.info.openedExistingConditionSet);
        return;
      }

      snackbar.showInfo(validationCopy.snackbars.info.createdNewConditionSet);
    },
    [
      conditionSets,
      createConditionSet,
      openConditionStudio,
      report,
      snackbar,
      state.validations,
      workspace,
    ]
  );

  const handleOpenConditionStudioFromHeader = useCallback(() => {
    if (defaultConditionStudioFamily) {
      handleOpenConditionStudio(defaultConditionStudioFamily);
      return;
    }

    const selectedConditionSetId = createConditionSet(defaultConditionStudioTargetKey);
    openConditionStudio({
      targetKey: defaultConditionStudioTargetKey,
      selectedConditionSetId,
      selectedValidationId: null,
      sourceFamilyId: null,
    });
    snackbar.showInfo(validationCopy.snackbars.info.createdNewConditionSet);
  }, [
    createConditionSet,
    defaultConditionStudioFamily,
    defaultConditionStudioTargetKey,
    handleOpenConditionStudio,
    openConditionStudio,
    snackbar,
  ]);

  const capabilityNote =
    !state.runtimeCapabilities.canHighlightElements ||
    !state.runtimeCapabilities.canReconnect ||
    !state.runtimeCapabilities.canQuickFix
      ? validationCopy.header.capabilityNote(state.runtimeCapabilities.mode)
      : undefined;
  const activeRunProgress =
    state.runProgress && (state.loading || state.runProgress.phase === 'error')
      ? state.runProgress
      : null;

  const handleCopyIssueList = async (family: ValidationIssueFamily) => {
    const text = buildIssueClipboardText(family);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        snackbar.showSuccess(validationCopy.snackbars.success.issueCopied);
        return;
      }
    } catch (error) {
      console.debug('Clipboard write failed, falling back to console copy text.', error);
    }

    console.info('[ValidationIssueDetailPanel] Copy this issue text manually:\n' + text);
    snackbar.showInfo(validationCopy.snackbars.info.clipboardUnavailable);
  };

  const detailFamily =
    repairDetailContext?.familyId
      ? allFamilies.find((family) => family.id === repairDetailContext.familyId) ?? null
      : null;

  const handleRunValidationScoped = useCallback(
    async (validationIds: number[]) => {
      await handleRunValidation(validationIds, {
        forceFresh: true,
        mergeIntoCurrentReport: true,
      });
    },
    [handleRunValidation]
  );

  const handleRunFocusedValidation = useCallback(
    async (family: ValidationIssueFamily) => {
      if (family.validationIds.length === 0) {
        return;
      }

      const primaryValidationId = family.validationIds.reduce((bestId, candidateId) =>
        resolveValidationIdCriticalityRank(candidateId) < resolveValidationIdCriticalityRank(bestId)
          ? candidateId
          : bestId
      );

      await handleRunValidation([primaryValidationId], {
        forceFresh: true,
        mergeIntoCurrentReport: true,
      });
    },
    [handleRunValidation]
  );

  const validationTourSteps = useMemo(
    () =>
      buildValidationTourSteps({
        compactMode: !showRailInline,
        inlineDetailMode: showInlineDetail,
        refs: {
          headerRef,
          railToggleRef,
          headerRunContextRef,
          headerActionsRef,
          headerGuideRef,
          headerConditionStudioRef,
          headerConfigureRef,
          headerRunRef,
          headerDetailToggleRef: detailToggleRef,
          railRef,
          railSearchRef,
          railFiltersRef,
          railRepairAreaRef,
          railPassedRef,
          shellRailResizeRef,
          shellDetailResizeRef,
          issueListRef,
          queueKeyboardHelpRef,
          queueSelectedCardRef,
          queueSelectedCardActionRef,
          detailRef,
          detailRecommendedRef,
          detailQuickFixRef,
          detailClassificationRef,
          detailFallbackGuidanceRef,
          detailAlternatePathsRef,
          detailImpactSummaryRef,
          detailMoreDetailsRef,
          detailAffectedItemsRef,
          detailTopAffectedTypesRef,
          detailSampleInstancesRef,
          detailRawIssueDetailsRef,
          detailCopyActionRef,
        },
      }),
    [showInlineDetail, showRailInline]
  );
  const validationGuideContent = useMemo(
    () =>
      buildValidationGuideContent({
        unresolvedFamilyCount: workspace.unresolvedFamilyCount,
        totalIssueCount: workspace.totalIssueCount,
        selectedValidationCount: state.selectedValidations.length,
        totalValidationCount: state.validations.length,
        focusedSystemName: focusedSystem?.name,
      }),
    [
      focusedSystem?.name,
      state.selectedValidations.length,
      state.validations.length,
      workspace.totalIssueCount,
      workspace.unresolvedFamilyCount,
    ]
  );
  const activeValidationTourStepId = tourOpen ? validationTourSteps[tourStep]?.id ?? null : null;
  const shouldRenderTourRailDrawer =
    Boolean(tourOpen && !showRailInline && activeValidationTourStepId?.startsWith('rail'));
  const shouldRenderTourDetailInline =
    Boolean(
      showDrawerDetail &&
        workspace.selectedFamily &&
        activeValidationTourStepId &&
        (activeValidationTourStepId.startsWith('detail') ||
          activeValidationTourStepId === 'shell-detail-resizer')
    );

  useEffect(() => {
    if (
      !requestedValidationSurface ||
      requestedValidationSurface === 'console' ||
      e2eValidationSurfaceAppliedRef.current ||
      !canRenderValidationWorkspace
    ) {
      return;
    }

    let isCancelled = false;
    const openRequestedValidationSurface = async () => {
      if (
        (requestedValidationSurface === 'repairDetail' ||
          requestedValidationSurface === 'conditionStudio') &&
        !state.report &&
        state.selectedValidations.length > 0
      ) {
        await ensureValidationPrimed();
      }

      if (isCancelled || e2eValidationSurfaceAppliedRef.current) {
        return;
      }

      if (requestedValidationSurface === 'configure') {
        if (state.validations.length === 0) {
          return;
        }

        handleCloseTour();
        setGuideOpen(false);
        state.setShowConfigDialog(true);
        e2eValidationSurfaceAppliedRef.current = true;
        return;
      }

      const candidateFamilies =
        workspace.allFamilies.length > 0 ? workspace.allFamilies : workspace.filteredFamilies;
      const repairDetailFamiliesWithItems = candidateFamilies.map((family) => ({
        family,
        items: buildValidationRepairDetailItems(family),
      }));
      const matchesRequestedRepairPath = (item: ReturnType<typeof buildValidationRepairDetailItems>[number]) =>
        requestedValidationRepairPath === 'direct'
          ? item.quickFixes.length > 0
          : requestedValidationRepairPath === 'guided'
            ? item.quickFixes.length === 0 && item.guidedAction !== null
            : item.quickFixes.length === 0 && item.guidedAction === null;
      const repairDetailCandidate =
        requestedValidationSurface === 'repairDetail'
          ? repairDetailFamiliesWithItems.find(({ items }) => items.some(matchesRequestedRepairPath))
          : null;
      const targetFamily =
        repairDetailCandidate?.family ?? candidateFamilies[0] ?? null;
      if (!targetFamily) {
        return;
      }

      handleCloseTour();
      setGuideOpen(false);
      workspace.setShowPassedOnly(false);
      workspace.setSelectedFamilyId(targetFamily.id);

      if (requestedValidationSurface === 'repairDetail') {
        const repairItems = repairDetailCandidate?.items ?? [];
        const selectedRepairItemId = repairItems.find(matchesRequestedRepairPath)?.id ?? null;
        openRepairDetail({
          familyId: targetFamily.id,
          selectedItemId: selectedRepairItemId,
          activeTab: 'recommended',
          surgeryMode: false,
        });
        e2eValidationSurfaceAppliedRef.current = true;
        return;
      }

      openConditionStudio({
        targetKey: 'required-export',
        selectedConditionSetId: null,
        selectedValidationId: null,
        sourceFamilyId: targetFamily.id,
      });
      e2eValidationSurfaceAppliedRef.current = true;
    };

    void openRequestedValidationSurface();

    return () => {
      isCancelled = true;
    };
  }, [
    canRenderValidationWorkspace,
    ensureValidationPrimed,
    handleCloseTour,
    openConditionStudio,
    openRepairDetail,
    requestedValidationRepairPath,
    requestedValidationSurface,
    state,
    workspace,
  ]);

  if (!state.isReady) {
    if (state.hasCheckedWebView && !state.isWebView2) {
      return <ValidationEmptyState variant="not-connected" />;
    }

    return <ValidationEmptyState variant="connecting" />;
  }

  if (state.documentInfoChecked && !state.documentInfo?.DocumentName) {
    return <ValidationEmptyState variant="no-document" />;
  }

  const triageRail = (
    <ValidationTriageRail
      containerRef={railRef}
      workspace={workspace}
      activeFilters={activeFilters}
      onClearFilters={handleClearFilters}
      tourRefs={{ searchRef: railSearchRef, filtersRef: railFiltersRef, repairAreaRef: railRepairAreaRef, passedRef: railPassedRef }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {activeRunProgress ? (
        <ValidationRunProgressWindowRequest progress={activeRunProgress} />
      ) : null}
      {state.report ? (
        <>
          {detailFamily ? (
            <Suspense fallback={<RepairWorkspaceLoadingFallback />}>
              <ValidationRepairDetailPage
                family={detailFamily}
                selectedItemId={repairDetailContext?.selectedItemId ?? null}
                activeTab={repairDetailContext?.activeTab ?? 'recommended'}
                surgeryMode={repairDetailContext?.surgeryMode ?? false}
                repairItemStates={repairItemStates}
                repairHistory={repairHistory}
                onBack={closeRepairDetail}
                onSelectItem={setRepairDetailSelectedItem}
                onChangeTab={setRepairDetailActiveTab}
                onSetSurgeryMode={setRepairSurgeryMode}
                onOpenConditionStudio={handleOpenConditionStudio}
                onExecutePrimaryAction={handleExecutePrimaryAction}
                onRunValidationScoped={handleRunValidationScoped}
                onHighlightElements={state.handleHighlightElements}
                onReconnectPair={state.handleReconnectPair}
                onReconnectAll={state.handleReconnectAll}
                onCopyIssueList={handleCopyIssueList}
                onAddRepairHistory={addRepairHistoryEntry}
                onSetRepairItemState={setRepairItemState}
                canHighlightElements={state.runtimeCapabilities.canHighlightElements}
                registerActiveActions={registerActiveValidationActions}
              />
            </Suspense>
          ) : conditionStudioContext ? (
            <Suspense fallback={<ValidationEmptyState variant="loading" />}>
              <ValidationConditionStudioPage
                report={state.report}
                validations={state.validations}
                selectedValidations={state.selectedValidations}
                onBack={() => {}}
              />
            </Suspense>
          ) : (
            <>
              <Box ref={headerRef} data-testid="validation-tour-target-header">
                <ValidationHeader
                  showBackButton={!embedded && Boolean(onGoBack)}
                  onGoBack={onGoBack}
                  embedded={embedded}
                  documentName={state.documentInfo?.DocumentName}
                  lastValidationLabel={lastValidationLabel}
                  unresolvedFamilyCount={workspace.unresolvedFamilyCount}
                  totalIssueCount={workspace.totalIssueCount}
                  criticalCount={workspace.severityCounts.critical}
                  errorCount={workspace.severityCounts.error}
                  warningCount={workspace.severityCounts.warning}
                  loading={state.loading}
                  onRunValidation={handleRunValidationFresh}
                  onOpenConditionStudio={handleOpenConditionStudioFromHeader}
                  onShowConfig={() => state.setShowConfigDialog(true)}
                  canConfigureMappings={state.runtimeCapabilities.canConfigureMappings}
                  selectedValidationCount={state.selectedValidations.length}
                  totalValidationCount={state.validations.length}
                  onOpenRail={() => setRailOpen(true)}
                  onToggleDetail={() => setDetailOpen((current) => !current)}
                  showRailToggle={!showRailInline}
                  showDetailToggle={!showInlineDetail && Boolean(workspace.selectedFamily)}
                  detailOpen={showInlineDetail || detailOpen || shouldRenderTourDetailInline}
                  focusedSystemName={focusedSystem?.name}
                  focusedElementCount={focusedSystem?.elementIds.length ?? 0}
                  onClearFocusedSystem={clearFocusedSystem}
                  capabilityNote={capabilityNote}
                  onOpenGuide={embedded ? undefined : handleOpenGuide}
                  tourRefs={{
                    railToggleRef,
                    detailToggleRef,
                    runContextRef: headerRunContextRef,
                    actionsRef: headerActionsRef,
                    guideActionRef: headerGuideRef,
                    conditionStudioActionRef: headerConditionStudioRef,
                    configureActionRef: headerConfigureRef,
                    runActionRef: headerRunRef,
                  }}
                />
              </Box>
              {showDrawerDetail &&
                !showInlineDetail &&
                !shouldRenderTourDetailInline &&
                !detailFamily &&
                !conditionStudioContext &&
                Boolean(workspace.selectedFamily) &&
                !detailOpen && (
                  <Box
                    data-testid="validation-detail-drawer-hint"
                    sx={{
                      px: { xs: 0.75, md: 0.9 },
                      py: 0.45,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.32 : 0.18)}`,
                      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.86 : 0.38),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={layoutGap.dense}
                      alignItems="center"
                      justifyContent="space-between"
                      useFlexGap
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <CaptionText component="span" sx={{ color: 'text.primary' }}>
                          Issue details hidden
                        </CaptionText>
                        <CaptionText component="span" sx={{
display: { xs: 'none', sm: 'inline' },
                            ml: 0.65,
                            color: 'text.secondary',
minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
}}>
                          {`"${workspace.selectedFamily?.title ?? ''}" selected. Open details for impact and next step.`}
                        </CaptionText>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailOpen(true)}
                          sx={{ minHeight: 26, px: 1.1, whiteSpace: 'nowrap', backgroundColor: alpha(theme.palette.background.paper, 0.5) }}
                        >
                          Show details
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                )}

              <WorkspacePageShell
                rail={showRailInline ? triageRail : undefined}
                storageKey={`pipeex-validation-shell-${embedded ? 'embedded' : 'standalone'}-v1`}
                railResizeLabel={validationCopy.pageShell.resizeRail}
                detailResizeLabel={validationCopy.pageShell.resizeDetail}
                tourRefs={{
                  railResizeRef: shellRailResizeRef,
                  detailResizeRef: shellDetailResizeRef,
                }}
                content={
                  <Box
                    ref={issueListRef}
                    data-testid="validation-tour-target-queue"
                    sx={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      height: '100%',
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <ValidationIssueList
                      families={workspace.filteredFamilies}
                      displayNumbersByFamilyId={displayNumbersByFamilyId}
                      selectedFamilyId={workspace.selectedFamilyId}
                      onSelectFamily={handleSelectFamily}
                      onExecutePrimaryAction={handleExecutePrimaryAction}
                      showPassedOnly={workspace.showPassedOnly}
                      passedChecks={workspace.visiblePassedChecks}
                      hasActiveFilters={hasActiveFilters}
                      onShowPassedOnly={() => workspace.setShowPassedOnly(true)}
                      tourRefs={{
                        selectedCardRef: queueSelectedCardRef,
                        selectedCardActionRef: queueSelectedCardActionRef,
                      }}
                    />
                  </Box>
                }
                detail={
                  showInlineDetail || (!showDrawerDetail && detailOpen) || shouldRenderTourDetailInline ? (
                    <Box
                      ref={detailRef}
                      data-testid="validation-tour-target-detail"
                      sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
                    >
                      <ValidationIssueDetailPanel
                        family={workspace.selectedFamily}
                        displayNumber={selectedFamilyDisplayNumber}
                        onExecutePrimaryAction={handleExecutePrimaryAction}
                        onOpenRepairDetail={handleOpenRepairDetail}
                        onOpenConditionStudio={handleOpenConditionStudio}
                        onHighlightElements={state.handleHighlightElements}
                        onReconnectPair={state.handleReconnectPair}
                        onReconnectAll={state.handleReconnectAll}
                        onRunValidation={handleRunValidationFresh}
                        onRunValidationScoped={handleRunValidationScoped}
                        onRunFocusedValidation={handleRunFocusedValidation}
                        focusedValidationRunning={state.loading}
                        onCopyIssueList={handleCopyIssueList}
                        canHighlightElements={state.runtimeCapabilities.canHighlightElements}
                        canQuickFix={state.runtimeCapabilities.canQuickFix}
                        emptyStateTitle={detailEmptyState.title}
                        emptyStateDescription={detailEmptyState.description}
                        emptyStateSteps={detailEmptyState.steps}
                        emptyStateActionLabel={detailEmptyState.emptyStateActionLabel}
                        onEmptyStateAction={detailEmptyState.onEmptyStateAction}
                        tourRefs={{
                          recommendedRef: detailRecommendedRef,
                          quickFixRef: detailQuickFixRef,
                          classificationRef: detailClassificationRef,
                          fallbackGuidanceRef: detailFallbackGuidanceRef,
                          alternatePathsRef: detailAlternatePathsRef,
                          impactSummaryRef: detailImpactSummaryRef,
                          moreDetailsRef: detailMoreDetailsRef,
                          affectedItemsRef: detailAffectedItemsRef,
                          topAffectedTypesRef: detailTopAffectedTypesRef,
                          sampleInstancesRef: detailSampleInstancesRef,
                          rawIssueDetailsRef: detailRawIssueDetailsRef,
                          copyActionRef: detailCopyActionRef,
                        }}
                      />
                    </Box>
                  ) : undefined
                }
              />
            </>
          )}

          {!detailFamily && !conditionStudioContext && !showRailInline && (
            <Drawer
              anchor="left"
              open={railOpen || shouldRenderTourRailDrawer}
              onClose={() => setRailOpen(false)}
              variant={useInlineTourDrawers ? 'persistent' : 'temporary'}
              hideBackdrop={useInlineTourDrawers}
              ModalProps={{
                keepMounted: true,
                disableAutoFocus: useInlineTourDrawers,
                disableEnforceFocus: useInlineTourDrawers,
                disableRestoreFocus: useInlineTourDrawers,
                disablePortal: useInlineTourDrawers,
              }}
              PaperProps={{
                sx: getWorkspaceDrawerPaperSx(theme, 'left'),
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  minHeight: 0,
                  width: { xs: '100vw', sm: 'clamp(248px, 30vw, 288px)' },
                  maxWidth: '100vw',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {triageRail}
              </Box>
            </Drawer>
          )}

          {!detailFamily && !conditionStudioContext && showDrawerDetail && !shouldRenderTourDetailInline && (
            <Drawer
              anchor="right"
              open={detailOpen && Boolean(workspace.selectedFamily)}
              onClose={() => setDetailOpen(false)}
              variant={useInlineTourDrawers ? 'persistent' : 'temporary'}
              hideBackdrop={useInlineTourDrawers}
              ModalProps={{
                keepMounted: true,
                disableAutoFocus: useInlineTourDrawers,
                disableEnforceFocus: useInlineTourDrawers,
                disableRestoreFocus: useInlineTourDrawers,
                disablePortal: useInlineTourDrawers,
              }}
              PaperProps={{
                sx: getWorkspaceDrawerPaperSx(theme, 'right'),
              }}
            >
              <Box
                ref={detailRef}
                data-testid="validation-tour-target-detail"
                sx={{
                  width: { xs: '100vw', sm: 'clamp(420px, 46vw, 520px)' },
                  maxWidth: '100vw',
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ValidationIssueDetailPanel
                  family={workspace.selectedFamily}
                  displayNumber={selectedFamilyDisplayNumber}
                  onExecutePrimaryAction={handleExecutePrimaryAction}
                  onOpenRepairDetail={handleOpenRepairDetail}
                  onOpenConditionStudio={handleOpenConditionStudio}
                  onHighlightElements={state.handleHighlightElements}
                  onReconnectPair={state.handleReconnectPair}
                  onReconnectAll={state.handleReconnectAll}
                  onRunValidation={handleRunValidationFresh}
                  onRunValidationScoped={handleRunValidationScoped}
                  onRunFocusedValidation={handleRunFocusedValidation}
                  focusedValidationRunning={state.loading}
                  onCopyIssueList={handleCopyIssueList}
                  canHighlightElements={state.runtimeCapabilities.canHighlightElements}
                  canQuickFix={state.runtimeCapabilities.canQuickFix}
                  emptyStateTitle={detailEmptyState.title}
                  emptyStateDescription={detailEmptyState.description}
                  emptyStateSteps={detailEmptyState.steps}
                  emptyStateActionLabel={detailEmptyState.emptyStateActionLabel}
                  onEmptyStateAction={detailEmptyState.onEmptyStateAction}
                  tourRefs={{
                    recommendedRef: detailRecommendedRef,
                    quickFixRef: detailQuickFixRef,
                    classificationRef: detailClassificationRef,
                    fallbackGuidanceRef: detailFallbackGuidanceRef,
                    alternatePathsRef: detailAlternatePathsRef,
                    impactSummaryRef: detailImpactSummaryRef,
                    moreDetailsRef: detailMoreDetailsRef,
                    affectedItemsRef: detailAffectedItemsRef,
                    topAffectedTypesRef: detailTopAffectedTypesRef,
                    sampleInstancesRef: detailSampleInstancesRef,
                    rawIssueDetailsRef: detailRawIssueDetailsRef,
                    copyActionRef: detailCopyActionRef,
                  }}
                />
              </Box>
            </Drawer>
          )}
        </>
      ) : state.loading ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1.5, md: 2 },
          }}
        >
          <Box sx={{ width: 'min(680px, 100%)' }}>
            <ValidationEmptyState variant="loading" />
          </Box>
        </Box>
      ) : state.hasStaleReport ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
           <ValidationEmptyState
             variant="stale"
             onRun={handleRunValidationFresh}
             disableRun={state.selectedValidations.length === 0}
           />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
           <ValidationEmptyState
             variant="empty"
             onRun={handleRunValidationFresh}
             disableRun={state.selectedValidations.length === 0}
           />
        </Box>
      )}

      {state.runtimeCapabilities.canConfigureMappings && (
        <ConfigurationDialog
          open={state.showConfigDialog}
          onClose={() => state.setShowConfigDialog(false)}
          validations={state.validations}
          selectedValidations={state.selectedValidations}
          onToggleValidation={state.handleToggleValidation}
          onSelectAll={state.handleSelectAll}
          onDeselectAll={state.handleDeselectAll}
        />
      )}
      <ReconnectionDialog
        open={state.reconnectDialogOpen}
        onClose={state.closeReconnectDialog}
        result={state.reconnectionResult}
        validationSettings={validationSettings}
        validationSettingsSaving={state.validationSettingsSaving ?? false}
        onUpdateReconnectGapMargin={state.handleUpdateReconnectGapMargin}
      />
      <ValidationGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        onStartTour={handleOpenTour}
        content={validationGuideContent}
      />
      <WorkspaceSpotlightTour
        open={tourOpen}
        steps={validationTourSteps}
        stepIndex={tourStep}
        onBack={() => setTourStep((current) => Math.max(0, current - 1))}
        onNext={() =>
          setTourStep((current) => Math.min(validationTourSteps.length - 1, current + 1))
        }
        onJumpToStep={setTourStep}
        onClose={handleCloseTour}
        dialogLabel="Validation workspace tour"
        showArrows
        chromeTargets={
          typeof window !== 'undefined' && window.chrome?.webview
            ? validationHostChromeTargets
            : undefined
        }
      />
    </Box>
  );
}
